import React, { useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import DeleteIcon from '@mui/icons-material/Delete';
import CustomCard from '../../Shared/Components/CustomCard/CustomCard';
import { buildApproveSupplierPrepaymentApContractorRequest } from '../Service/bankDocumentsRequestBuilder';
import type { ApproveSupplierPrepaymentApContractorResultDto } from '../Types/bankDocument.types';
import {
  useApproveSupplierPrepaymentApContractorMutation,
  useCreateSupplierPrepaymentApContractorMutation,
  useDeleteSupplierPrepaymentApContractorMutation,
  useGetSupplierPrepaymentApContractorsAdminListQuery,
} from '../Service/bankDocumentsService';

const normalizeNip = (value: string): string => value.replace(/[^0-9]/g, '');

const formatPolishCountLabel = (
  count: number,
  singular: string,
  paucal: string,
  plural: string
): string => {
  const absoluteCount = Math.abs(count);
  const lastTwoDigits = absoluteCount % 100;
  const lastDigit = absoluteCount % 10;

  if (absoluteCount === 1) {
    return `${count} ${singular}`;
  }

  if (lastTwoDigits >= 12 && lastTwoDigits <= 14) {
    return `${count} ${plural}`;
  }

  if (lastDigit >= 2 && lastDigit <= 4) {
    return `${count} ${paucal}`;
  }

  return `${count} ${plural}`;
};

const buildApprovalSuccessMessage = (
  apContractorName: string,
  result: ApproveSupplierPrepaymentApContractorResultDto
): string => {
  const promotedCount = result.promotedExpenseInvoiceIds.length;
  const affectedBookingCount = result.affectedBookingIds.length;

  if (promotedCount === 0 && affectedBookingCount === 0) {
    return `Kontrahent AP „${apContractorName}” został zatwierdzony.`;
  }

  return `Kontrahent AP „${apContractorName}” został zatwierdzony. Promowano ${formatPolishCountLabel(
    promotedCount,
    'draft kosztowy',
    'drafty kosztowe',
    'draftów kosztowych'
  )}, dotknięto ${formatPolishCountLabel(
    affectedBookingCount,
    'booking',
    'bookingi',
    'bookingów'
  )}.`;
};

const SupplierPrepaymentApContractorsPage: React.FC = () => {
  const { data, isLoading, isFetching, isError } =
    useGetSupplierPrepaymentApContractorsAdminListQuery();
  const [createApContractor, { isLoading: isCreating }] =
    useCreateSupplierPrepaymentApContractorMutation();
  const [approveApContractor, { isLoading: isApproving }] =
    useApproveSupplierPrepaymentApContractorMutation();
  const [deleteApContractor, { isLoading: isDeleting }] =
    useDeleteSupplierPrepaymentApContractorMutation();

  const [name, setName] = useState('');
  const [nip, setNip] = useState('');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<{ id: number; name: string } | null>(null);
  const [approvingApContractorId, setApprovingApContractorId] = useState<number | null>(null);

  const rows = useMemo(() => data ?? [], [data]);
  const isRowActionPending = isDeleting || isApproving || approvingApContractorId !== null || isFetching;

  const handleCreate = async (): Promise<void> => {
    setErrorMessage(null);
    setSuccessMessage(null);

    const normalizedNip = normalizeNip(nip);
    if (!name.trim()) {
      setErrorMessage('Nazwa kontrahenta jest wymagana.');
      return;
    }

    if (normalizedNip.length !== 10) {
      setErrorMessage('NIP musi zawierać dokładnie 10 cyfr.');
      return;
    }

    try {
      await createApContractor({ name: name.trim(), nip: normalizedNip }).unwrap();
      setName('');
      setNip('');
      setSuccessMessage('Kontrahent AP został dodany.');
    } catch {
      setErrorMessage('Nie udało się dodać kontrahenta AP.');
    }
  };

  const handleApprove = async (apContractorId: number, apContractorName: string): Promise<void> => {
    if (isApproving || approvingApContractorId === apContractorId) {
      return;
    }

    setErrorMessage(null);
    setSuccessMessage(null);
    setApprovingApContractorId(apContractorId);

    try {
      const result = await approveApContractor(
        buildApproveSupplierPrepaymentApContractorRequest(apContractorId)
      ).unwrap();

      setSuccessMessage(buildApprovalSuccessMessage(apContractorName, result));
    } catch {
      setErrorMessage('Nie udało się zatwierdzić kontrahenta AP.');
    } finally {
      setApprovingApContractorId(null);
    }
  };

  const handleDelete = async (): Promise<void> => {
    if (!deleteTarget || isRowActionPending) {
      return;
    }

    setErrorMessage(null);
    setSuccessMessage(null);

    try {
      await deleteApContractor(deleteTarget.id).unwrap();
      setSuccessMessage('Kontrahent AP został dezaktywowany.');
      setDeleteTarget(null);
    } catch {
      setErrorMessage('Nie udało się dezaktywować kontrahenta AP.');
    }
  };

  return (
    <Stack spacing={3}>
      <CustomCard
        icon={<AccountBalanceWalletIcon color="primary" />}
        title="Kontrahenci AP"
        subheader="Zarządzanie kontrahentami używanymi w prepayment dostawców"
      >
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ md: 'flex-end' }}>
          <TextField
            label="Nazwa"
            value={name}
            onChange={(event) => setName(event.target.value)}
            fullWidth
          />
          <TextField
            label="NIP"
            value={nip}
            onChange={(event) => setNip(event.target.value)}
            inputProps={{ maxLength: 20 }}
            fullWidth
          />
          <Button variant="contained" onClick={handleCreate} disabled={isCreating}>
            {isCreating ? 'Dodawanie…' : 'Dodaj'}
          </Button>
        </Stack>

        {successMessage && (
          <Alert severity="success" sx={{ mt: 2 }}>
            {successMessage}
          </Alert>
        )}
        {errorMessage && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {errorMessage}
          </Alert>
        )}
      </CustomCard>

      <CustomCard title="Lista kontrahentów AP" icon={<AccountBalanceWalletIcon color="primary" />}>
        {(isLoading || isFetching) && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        )}

        {isError && <Alert severity="error">Nie udało się pobrać listy kontrahentów AP.</Alert>}

        {!isLoading && !isError && rows.length === 0 && (
          <Typography variant="body2" color="text.secondary">
            Brak kontrahentów AP do wyświetlenia.
          </Typography>
        )}

        {!isLoading && !isError && rows.length > 0 && (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Nazwa</TableCell>
                <TableCell>NIP</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Zatwierdzenie</TableCell>
                <TableCell>Źródło</TableCell>
                <TableCell align="right">Akcje</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row) => (
                <TableRow key={row.id} hover>
                  <TableCell>{row.name}</TableCell>
                  <TableCell>{row.nip}</TableCell>
                  <TableCell>{row.isActive ? 'Aktywny' : 'Nieaktywny'}</TableCell>
                  <TableCell>{row.isApproved ? 'Zatwierdzony' : 'Wymaga zatwierdzenia'}</TableCell>
                  <TableCell>{row.autoCreatedFromKsef ? 'KSeF' : 'Manualne'}</TableCell>
                  <TableCell align="right">
                    <Stack direction="row" spacing={1} justifyContent="flex-end">
                      {!row.isApproved ? (
                        <Button
                          color="primary"
                          size="small"
                          startIcon={<CheckCircleOutlineIcon />}
                          disabled={!row.isActive || isRowActionPending}
                          onClick={() => handleApprove(row.id, row.name)}
                        >
                          {approvingApContractorId === row.id ? 'Zatwierdzanie…' : 'Zatwierdź'}
                        </Button>
                      ) : null}
                        <Button
                          color="error"
                          size="small"
                          startIcon={<DeleteIcon />}
                          disabled={!row.isActive || isRowActionPending}
                          onClick={() => setDeleteTarget({ id: row.id, name: row.name })}
                        >
                          Dezaktywuj
                        </Button>
                    </Stack>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CustomCard>

      <Dialog
        open={deleteTarget !== null}
        onClose={isRowActionPending ? undefined : () => setDeleteTarget(null)}
      >
        <DialogTitle>Dezaktywacja kontrahenta AP</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Czy na pewno chcesz dezaktywować kontrahenta „{deleteTarget?.name}”? Operacja ukryje go
            z listy aktywnych kontrahentów.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)} disabled={isRowActionPending}>
            Anuluj
          </Button>
          <Button color="error" onClick={handleDelete} disabled={isRowActionPending}>
            {isDeleting ? 'Dezaktywacja…' : 'Dezaktywuj'}
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
};

export default SupplierPrepaymentApContractorsPage;
